import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle,
  AlertCircle,
  X,
  Search,
  MapPin,
  Calendar,
  DollarSign,
  Bell,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Clock,
  CreditCard,
  AlertTriangle,
  Users,
} from "lucide-react";
import {
  fetchOverdueInstallments,
  payInstallmentAsync,
} from "../store/slices/installmentSlice";
import { fetchAllUsers } from "../store/slices/authSlice";
import { getAllBookings } from "../store/slices/bookingSlice";
import { addNotification } from "../store/slices/notificationSlice";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { motion, AnimatePresence } from "framer-motion";
import FollowUpModal from "./FollowUpModal";

const OverduePayments = () => {
  const dispatch = useDispatch();
  const [reminderSent, setReminderSent] = useState({});
  const [viewDetails, setViewDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [selectedFollowUpData, setSelectedFollowUpData] = useState(null);
  const itemsPerPage = 6;

  const { activeSociety, gradient, theme } = useSidebarContext();

  const colors = useMemo(() => {
    return {
      text: theme?.tailwind?.text9,
      textMuted: theme.tailwind.text4,
      textLight: theme.tailwind.text,
      textDim: theme?.tailwind?.text2,
      primary: "text-violet-400",
      accent: "text-fuchsia-400",
      border: theme?.tailwind?.border3,
      borderAccent: theme?.tailwind?.border1 || "border-indigo-300/20",
      borderButton: theme?.tailwind?.border15 || "border-indigo-400/30",
      borderButtonHover:
        theme?.tailwind?.hoverB || "hover:border-indigo-400/60",
      bg:
        gradient ||
        "bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900",
      bgCard: theme?.tailwind?.bg2,
      bgCardHover: "hover:bg-slate-800/60",
      bgHover: theme?.tailwind?.bgHover || "hover:bg-slate-700/50",
      bgButton: theme?.tailwind?.bg25 || "bg-indigo-500/15",
      bgButtonAccent: theme.tailwind.bgGradient11,
      bgButtonHover: theme?.tailwind?.bgHover13 || "hover:bg-indigo-500/25",
      bgDropdown: "bg-slate-800/95",
      bgYellow: "bg-amber-400/15",
      bgOrange: "bg-orange-400/20",
      bgRed: "bg-rose-500/20",
      placeholder: theme?.tailwind?.placeHolder || "placeholder-indigo-300/40",
      focus: theme?.tailwind?.focusRing1 || "focus:ring-indigo-500/50",
      focusAccent: "focus:ring-fuchsia-500/50",
      shadow: theme?.tailwind?.shadow || "shadow-slate-900/30",
      shadowAccent: theme?.tailwind?.shadowing || "shadow-indigo-500/20",
      shadowHover: "hover:shadow-indigo-500/30",
      ringAccent: "ring-indigo-500/20",
    };
  }, [theme, gradient]);

  const {
    overdueInstallments = [],
    status: installmentsStatus,
    error: installmentsError,
  } = useSelector((state) => state.installments || {});
  const {
    users = [],
    status: userStatus,
    error: userError,
  } = useSelector((state) => state.auth || {});
  const {
    bookings = [],
    loading: bookingsLoading,
    error: bookingsError,
  } = useSelector((state) => state.booking || {});

  useEffect(() => {
    dispatch(fetchOverdueInstallments());
    dispatch(getAllBookings());
    dispatch(fetchAllUsers());
  }, [dispatch]);

  // Debug logging for troubleshooting
  // useEffect(() => {
  //   console.log('Raw overdueInstallments:', overdueInstallments);
  //   // console.log('Filtered overdueInstallments:', filteredInstallments);
  //   console.log('Active Society:', activeSociety);
  //   console.log('Users:', users);
  // }, [overdueInstallments, activeSociety, users]);

  // Helper function to extract customer ID from installment (handles both ObjectId and populated object)
  const getCustomerIdFromInstallment = (installment) => {
    if (!installment?.customer_id) return null;
    return typeof installment.customer_id === 'object' && installment.customer_id?._id
      ? installment.customer_id._id.toString()
      : installment.customer_id.toString();
  };

  // Helper function to find customer from users array by installment
  const findCustomerByInstallment = (installment, usersArray) => {
    const customerId = getCustomerIdFromInstallment(installment);
    if (!customerId) return null;
    return usersArray.find(
      (user) => user._id?.toString() === customerId && user.role === "Customer"
    );
  };

  const filteredInstallments = useMemo(() => {
    // Early return if no active society or no overdue installments
    if (!activeSociety || !overdueInstallments || overdueInstallments.length === 0) {
      return [];
    }

    const societyId = activeSociety._id?.toString();

    // Filter installments by plot's society_id first (more reliable than customer matching)
    let filtered = overdueInstallments.filter((installment) => {
      // FIX: Explicitly exclude cancelled installments (from canceled plots)
      if (installment.status === 'Cancelled' || installment.transfer_type === 'cancel') {
        return false;
      }

      // Filter by plot's society_id if available
      const plotSocietyId = installment.plot_id?.society_id;
      if (plotSocietyId) {
        const plotSocietyIdStr = typeof plotSocietyId === 'object' ? plotSocietyId._id?.toString() : plotSocietyId.toString();
        if (plotSocietyIdStr !== societyId) {
          return false;
        }
      } else {
        // Fallback: filter by customer's societies if plot society_id is not available
        const customerId = getCustomerIdFromInstallment(installment);
        if (!customerId) return false;

        const customer = users.find(
          (user) =>
            user._id?.toString() === customerId &&
            user.role === "Customer" &&
            user.societies &&
            user.societies.some(
              (society) => {
                const societyIdStr = typeof society === 'string' ? society : society._id?.toString();
                return societyIdStr === societyId;
              }
            )
        );

        if (!customer) return false;
      }

      // Filter by search term if provided
      if (searchTerm) {
        const customerId = getCustomerIdFromInstallment(installment);
        const customer = users.find(u => u._id?.toString() === customerId);
        const customerName = (customer?.customerData?.name || installment.customer_name || '').toLowerCase();
        if (!customerName.includes(searchTerm.toLowerCase())) {
          return false;
        }
      }

      return true;
    });

    // Apply status filtering
    if (filterStatus !== "all") {
      filtered = filtered.filter((installment) => {
        const overdueMonths = Math.ceil(
          (Date.now() - new Date(installment.due_date).getTime()) /
          (1000 * 60 * 60 * 24 * 30)
        );

        if (filterStatus === "critical" && overdueMonths > 2) return true;
        if (filterStatus === "warning" && overdueMonths === 2) return true;
        if (filterStatus === "attention" && overdueMonths === 1) return true;
        return false;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === "date") {
        const dateA = new Date(a.due_date).getTime();
        const dateB = new Date(b.due_date).getTime();
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (sortBy === "amount") {
        return sortOrder === "asc" ? a.amount - b.amount : b.amount - a.amount;
      }

      if (sortBy === "customer") {
        const customerA = findCustomerByInstallment(a, users);
        const customerB = findCustomerByInstallment(b, users);
        const nameA = customerA?.customerData?.name || "";
        const nameB = customerB?.customerData?.name || "";
        return sortOrder === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      return 0;
    });

    return filtered;
  }, [
    overdueInstallments,
    activeSociety,
    searchTerm,
    filterStatus,
    sortBy,
    sortOrder,
    users,
  ]);

  // Log filteredInstallments for debugging
  useEffect(() => {
    // console.log('Filtered overdueInstallments:', filteredInstallments);
  }, [filteredInstallments]);

  const groupInstallmentsByPlot = useMemo(() => {
    const plotGroups = {};
    filteredInstallments.forEach((installment) => {
      const plotNumber = installment.plot_id?.plot_number;
      if (!plotNumber) return;
      if (!plotGroups[plotNumber]) plotGroups[plotNumber] = [];
      plotGroups[plotNumber].push(installment);
    });
    return plotGroups;
  }, [filteredInstallments]);

  // Search and filter functionality
  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    return users
      .filter(
        (user) => {
          if (!user.customerData?.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
          }
          return filteredInstallments.some((inst) => {
            // FIX: Explicitly exclude cancelled installments from search results
            // This ensures cancelled installments don't appear when searching for customers
            if (inst.status === 'Cancelled' || inst.transfer_type === 'cancel') {
              return false;
            }
            const customerId = getCustomerIdFromInstallment(inst);
            return customerId && user._id?.toString() === customerId;
          });
        }
      )
      .slice(0, 5);
  }, [searchTerm, users, filteredInstallments]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    const entries = Object.entries(groupInstallmentsByPlot);
    const startIndex = (currentPage - 1) * itemsPerPage;
    return entries.slice(startIndex, startIndex + itemsPerPage);
  }, [groupInstallmentsByPlot, currentPage]);

  const totalPages = Math.ceil(
    Object.keys(groupInstallmentsByPlot).length / itemsPerPage
  );

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`flex items-center justify-center h-8 w-8 rounded-lg text-sm font-medium transition-all duration-300 transform ${currentPage === i
            ? `${theme.tailwind.text13} ${colors.bgButtonAccent} shadow-lg scale-105`
            : `${colors.textMuted} bg-slate-800/40 hover:bg-slate-700/50`
            }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const handleSendReminder = async (installmentId, customerId, plotId, overdueAmount, overdueMonths) => {
    try {
      // Create overdue notifications using the API
      await Promise.all([
        fetch('/api/notifications', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId: customerId,
            message: `⚠️ Overdue Payment Alert: Your installment for Plot #${plotId} is ${overdueMonths} month(s) overdue. Amount: Rs. ${overdueAmount?.toLocaleString() || 'N/A'}`,
            type: "Overdue",
            recipientRole: "Customer",
          })
        }),
        fetch('/api/notifications', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId: null,
            message: `🚨 Overdue Payment: Plot #${plotId} is ${overdueMonths} month(s) overdue. Customer #${customerId}. Amount: Rs. ${overdueAmount?.toLocaleString() || 'N/A'}`,
            type: "Overdue",
            recipientRole: "Admin",
          })
        })
      ]);

      setReminderSent((prev) => ({
        ...prev,
        [installmentId]: Date.now(),
      }));
    } catch (error) {
      console.error('Error sending reminder notifications:', error);
    }
  };

  const getInstallmentSeverity = (installment) => {
    const overdueMonths = Math.ceil(
      (Date.now() - new Date(installment.due_date).getTime()) /
      (1000 * 60 * 60 * 24 * 30)
    );

    if (overdueMonths === 1)
      return {
        bgColor: colors.bgYellow,
        textColor: "text-amber-400",
        label: "Attention",
        borderColor: "border-amber-400/30",
        icon: <AlertCircle className="w-4 h-4 text-amber-400" />,
      };

    if (overdueMonths === 2)
      return {
        bgColor: colors.bgOrange,
        textColor: "text-orange-400",
        label: "Warning",
        borderColor: "border-orange-400/30",
        icon: <AlertTriangle className="w-4 h-4 text-orange-400" />,
      };

    return {
      bgColor: colors.bgRed,
      textColor: "text-rose-400",
      label: "Critical",
      borderColor: "border-rose-400/30",
      icon: <AlertCircle className="w-4 h-4 text-rose-400" />,
    };
  };

  const handleToggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleSetSortBy = (value) => {
    if (value === sortBy) {
      handleToggleSortOrder();
    } else {
      setSortBy(value);
      setSortOrder("asc");
    }
  };

  if (
    installmentsStatus === "loading" ||
    userStatus === "loading" ||
    bookingsLoading
  ) {
    return (
      <div
        className={`flex items-center justify-center h-96 ${colors.bg} rounded-3xl`}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="absolute inset-0 bg-indigo-400/20 blur-xl rounded-full animate-pulse"></div>
            <div className="flex items-center space-x-2">
              <div
                className={`w-4 h-4 rounded-full ${theme.tailwind.bg1} animate-bounce`}
              ></div>
              <div
                className={`w-4 h-4 rounded-full ${theme.tailwind.bg10} animate-bounce delay-100`}
              ></div>
              <div
                className={`w-4 h-4 rounded-full ${theme.tailwind.bg11} animate-bounce delay-200`}
              ></div>
            </div>
          </div>
          <p className={`${colors.text} text-lg`}>
            Loading overdue payments...
          </p>
        </div>
      </div>
    );
  }

  if (
    installmentsStatus === "failed" ||
    userStatus === "failed" ||
    bookingsError
  ) {
    return (
      <div
        className={`flex items-center justify-center h-64 ${colors.bg} rounded-3xl`}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="absolute inset-0 bg-rose-400/20 blur-xl rounded-full"></div>
            <AlertCircle className="w-12 h-12 text-rose-400 relative z-10" />
          </div>
          <div className="text-center">
            <p className="text-rose-400 text-lg font-medium">Error</p>
            <p className={colors.textMuted}>
              {installmentsError ||
                userError ||
                bookingsError ||
                "Unknown error"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isReminderDisabled = (installmentId) => {
    const reminderTimestamp = reminderSent[installmentId];
    return reminderTimestamp && Date.now() - reminderTimestamp < 86400000;
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-64 space-y-6 py-10">
      <div className="relative">
        <div className="absolute inset-0 bg-indigo-400/20 blur-2xl rounded-full"></div>
        <CheckCircle className={`w-20 h-20 text-indigo-400 relative z-10`} />
      </div>
      <div className="text-center">
        <h3 className={`${colors.textLight} text-2xl font-medium mb-2`}>
          All Caught Up!
        </h3>
        <p className={`${colors.textMuted} max-w-md`}>
          There are no overdue installments for{" "}
          {activeSociety?.name || "this society"}. All payments are up to date.
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <div
        className={`p-6 md:p-8 m-4 md:m-8 rounded-3xl ${colors.bg} shadow-2xl border ${colors.borderAccent} backdrop-blur-sm`}
      >
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-fuchsia-400/20 blur-lg rounded-full"></div>
                <div
                  className={`h-12 w-1 ${theme.tailwind.BgBottom} rounded-full relative z-10`}
                ></div>
              </div>
              <h2
                className={`text-3xl md:text-4xl font-bold ${colors.textLight} flex items-center gap-3`}
              >
                Overdue Payments
                <span
                  className={`text-lg md:text-xl font-normal ${theme.tailwind.text15}`}
                >
                  {activeSociety?.name || "No Active Society"}
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-3 ml-4">
              <div className={`${colors.textMuted} flex items-center gap-1`}>
                <Users className="w-4 h-4" />
                <span>
                  {Object.keys(groupInstallmentsByPlot).length} customers
                </span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-400/50"></div>
              <div className={`${colors.textMuted} flex items-center gap-1`}>
                <CreditCard className="w-4 h-4" />
                <span>{filteredInstallments.length} pending payments</span>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full lg:w-auto">
            {/* Search Bar */}
            <div className="relative w-full md:w-64">
              <div
                className={`flex items-center space-x-3 ${theme.tailwind.bg31} rounded-xl p-3 backdrop-blur-sm border ${theme.tailwind.border4} shadow-lg`}
              >
                <Search className={`w-5 h-5 ${colors.textMuted}`} />
                <input
                  type="text"
                  placeholder="Search by customer name..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(Boolean(e.target.value));
                  }}
                  className={`bg-transparent w-full ${colors.text} ${colors.placeholder} focus:outline-none`}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className={`${theme.tailwind.text13} ${theme.tailwind.hover} transition-colors`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              <AnimatePresence>
                {showDropdown && searchResults.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={`absolute z-10 w-full mt-2 ${colors.bgDropdown} rounded-xl shadow-2xl border ${colors.borderAccent} backdrop-blur-md overflow-hidden`}
                  >
                    {searchResults.map((user) => (
                      <div
                        key={user._id}
                        className={`p-4 hover:bg-indigo-500/10 cursor-pointer transition-all duration-300 border-b border-indigo-300/10 last:border-0`}
                        onClick={() => {
                          setSearchTerm(user.customerData.name);
                          setShowDropdown(false);
                        }}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="relative w-10 h-10">
                            <div className="absolute inset-0 bg-indigo-400/20 blur-md rounded-full"></div>
                            <img
                              src={
                                user.profile_picture ||
                                "/default-profile-pic.png"
                              }
                              alt={user.customerData.name}
                              className="w-10 h-10 rounded-full object-cover relative z-10 border-2 border-indigo-400/30"
                            />
                          </div>
                          <div>
                            <div className={`${colors.textLight} font-medium`}>
                              {user.customerData.name}
                            </div>
                            <div className={`${colors.textMuted} text-sm`}>
                              {user.customerData.contactInfo?.phone ||
                                "No phone"}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2 w-full md:w-auto">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-4 py-2 rounded-xl text-sm transition-all duration-300 flex-1 md:flex-none
                ${filterStatus === "all"
                    ? `${colors.bgButton} ${colors.text} border ${colors.borderButton}`
                    : `${theme.tailwind.bg31} ${colors.textMuted} border ${theme.tailwind.border3}`
                  }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterStatus("critical")}
                className={`px-4 py-2 rounded-xl text-sm transition-all duration-300 flex-1 md:flex-none
                ${filterStatus === "critical"
                    ? `bg-rose-500/15 text-rose-400 border border-rose-400/30`
                    : `${theme.tailwind.bg31} ${colors.textMuted} border ${theme.tailwind.border3}`
                  }`}
              >
                Critical
              </button>
              <button
                onClick={() => setFilterStatus("warning")}
                className={`px-4 py-2 rounded-xl text-sm transition-all duration-300 flex-1 md:flex-none
                ${filterStatus === "warning"
                    ? `bg-orange-500/15 text-orange-400 border border-orange-400/30`
                    : `${theme.tailwind.bg31} ${colors.textMuted} border ${theme.tailwind.border3}`
                  }`}
              >
                Warning
              </button>
              <button
                onClick={() => setFilterStatus("attention")}
                className={`px-4 py-2 rounded-xl text-sm transition-all duration-300 flex-1 md:flex-none
                ${filterStatus === "attention"
                    ? `bg-amber-500/15 text-amber-400 border border-amber-400/30`
                    : `${theme.tailwind.bg31} ${colors.textMuted} border ${theme.tailwind.border3}`
                  }`}
              >
                Attention
              </button>
            </div>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-6">
            <div className={`${colors.textMuted} text-sm`}>Sort by:</div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleSetSortBy("date")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-300 
                ${sortBy === "date"
                    ? `${colors.bgButtonAccent} ${theme.tailwind.text13}`
                    : `${colors.textMuted} hover:${theme.tailwind.text14}`
                  }`}
              >
                <Calendar className="w-4 h-4" />
                Date
                {sortBy === "date" && (
                  <span className="text-xs">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleSetSortBy("amount")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-300 
                ${sortBy === "amount"
                    ? `${colors.bgButtonAccent} ${theme.tailwind.text13}`
                    : `${colors.textMuted} hover:${theme.tailwind.text14}`
                  }`}
              >
                <DollarSign className="w-4 h-4" />
                Amount
                {sortBy === "amount" && (
                  <span className="text-xs">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleSetSortBy("customer")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-300 
                ${sortBy === "customer"
                    ? `${colors.bgButtonAccent} ${theme.tailwind.text13}`
                    : `${colors.textMuted} hover:${theme.tailwind.text14}`
                  }`}
              >
                <Users className="w-4 h-4" />
                Customer
                {sortBy === "customer" && (
                  <span className="text-xs">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          className={`rounded-2xl overflow-hidden border ${theme.tailwind.border3} backdrop-blur-sm ${theme.tailwind.bgGradient12}`}
        >
          {!filteredInstallments.length ? (
            <EmptyState />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${theme.tailwind.border3}`}>
                    <th
                      className={`p-5 text-left ${colors.textLight} font-medium`}
                    >
                      Customer
                    </th>
                    <th
                      className={`p-5 text-left ${colors.textLight} font-medium`}
                    >
                      Plot Details
                    </th>
                    <th
                      className={`p-5 text-left ${colors.textLight} font-medium`}
                    >
                      Status
                    </th>
                    <th
                      className={`p-5 text-left ${colors.textLight} font-medium`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map(([plotNumber, installments], index) => {
                    const customer = findCustomerByInstallment(installments[0], users);
                    const severity = getInstallmentSeverity(installments[0]);

                    return (
                      <motion.tr
                        key={plotNumber}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={`border-b ${theme.tailwind.border3} transition-colors duration-300 hover:bg-indigo-500/5`}
                      >
                        <td className="p-5">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <div className="absolute inset-0 bg-indigo-400/20 blur-md rounded-full"></div>
                              <img
                                src={
                                  customer?.profile_picture ||
                                  "/default-profile-pic.png"
                                }
                                alt={customer?.customerData?.name || "Customer"}
                                className="w-12 h-12 rounded-full object-cover relative z-10 border-2 border-indigo-400/30"
                              />
                            </div>
                            <div>
                              <div
                                className={`font-medium ${colors.textLight}`}
                              >
                                {customer?.customerData?.name || "Unknown"}
                              </div>
                              <div
                                className={`text-sm ${colors.textMuted} flex items-center gap-1.5`}
                              >
                                <span>
                                  {customer?.customerData?.contactInfo?.phone ||
                                    "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className={`space-y-1`}>
                            <div className="text-lg font-semibold flex items-center gap-2">
                              <MapPin
                                className={`w-4 h-4 ${theme.tailwind.text15}`}
                              />
                              <span className={colors.textLight}>
                                Plot #{plotNumber}
                              </span>
                            </div>
                            <div
                              className={`text-sm ${colors.textMuted} flex items-center gap-1.5`}
                            >
                              <Clock className="w-3.5 h-3.5" />
                              <span>
                                {new Date(
                                  installments[0].due_date
                                ).toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className={`flex flex-col items-start gap-1`}>
                            <div
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${severity.bgColor} ${severity.borderColor} border`}
                            >
                              {severity.icon}
                              <span
                                className={`text-sm font-medium ${severity.textColor}`}
                              >
                                {severity.label}
                              </span>
                            </div>
                            <div className={`text-sm ${colors.textMuted}`}>
                              {installments.length} overdue{" "}
                              {installments.length > 1 ? "payments" : "payment"}
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedFollowUpData({
                                  plotId: installments[0]?.plot_id?._id,
                                  bookingId: installments[0]?.booking_id,
                                  customerId: installments[0]?.customer_id
                                });
                                setIsFollowUpModalOpen(true);
                              }}
                              className={`px-4 py-2.5 rounded-xl border border-blue-500/30 text-blue-400 bg-blue-500/10 transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 ring-blue-500/20 text-sm font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20`}
                            >
                              <Clock className="w-4 h-4" />
                              Follow Ups
                            </button>
                            <button
                              onClick={() => setViewDetails(installments)}
                              className={`px-5 py-2.5 rounded-xl ${colors.bgButtonAccent} transition-all duration-300 ${theme.tailwind.text13} shadow-lg shadow-fuchsia-500/10 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 ring-fuchsia-500/20`}
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {filteredInstallments.length > 0 && (
          <div className="flex items-center justify-between mt-6">
            <div className={`${colors.textMuted} text-sm`}>
              Showing{" "}
              {Math.min(
                (currentPage - 1) * itemsPerPage + 1,
                Object.keys(groupInstallmentsByPlot).length
              )}{" "}
              to{" "}
              {Math.min(
                currentPage * itemsPerPage,
                Object.keys(groupInstallmentsByPlot).length
              )}{" "}
              of {Object.keys(groupInstallmentsByPlot).length} plots
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-300 ${currentPage === 1
                  ? `${colors.textDim} cursor-not-allowed`
                  : `${colors.textMuted} ${colors.bgHover} hover:text-indigo-300`
                  }`}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>

              {renderPageNumbers()}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-300 ${currentPage === totalPages
                  ? `${colors.textDim} cursor-not-allowed`
                  : `${colors.textMuted} ${colors.bgHover} hover:text-indigo-300`
                  }`}
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      <AnimatePresence>
        {viewDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`${theme.tailwind.bg16} border ${colors.borderAccent} rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]`}
            >
              {/* Fixed Header Section */}
              <div className="p-6 flex-shrink-0">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-2xl font-bold ${colors.textLight}`}>
                    Overdue Payment Details
                  </h3>
                  <button
                    onClick={() => setViewDetails(null)}
                    className={`${colors.textMuted} hover:${colors.textLight} transition-colors p-2 rounded-full ${colors.bgHover}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {viewDetails && viewDetails.length > 0 && (
                  <>
                    {/* Fixed Customer Information */}
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="relative">
                        <div className="absolute inset-0 bg-indigo-400/20 blur-md rounded-full"></div>
                        <img
                          src={
                            users.find(
                              (u) => u._id === viewDetails[0].customer_id
                            )?.profile_picture || "/default-profile-pic.png"
                          }
                          alt="Customer"
                          className="w-16 h-16 rounded-full object-cover relative z-10 border-2 border-indigo-400/30"
                        />
                      </div>
                      <div>
                        <div
                          className={`${colors.textLight} text-xl font-medium`}
                        >
                          {(() => {
                            const customer = findCustomerByInstallment(viewDetails[0], users);
                            return customer?.customerData?.name || "Unknown Customer";
                          })()}
                        </div>
                        <div
                          className={`${colors.textMuted} flex items-center gap-2`}
                        >
                          {(() => {
                            const customer = findCustomerByInstallment(viewDetails[0], users);
                            return (
                              <>
                                <span>
                                  {customer?.customerData?.contactInfo?.phone || "No phone"}
                                </span>
                                <div className="w-1 h-1 rounded-full bg-indigo-400/50"></div>
                                <span>
                                  {customer?.customerData?.contactInfo?.email || "No email"}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Fixed Plot Details */}
                    <div
                      className={`bg-slate-800/30 rounded-xl p-4 border ${colors.borderAccent} mb-6`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin
                            className={`w-5 h-5 ${theme.tailwind.text15}`}
                          />
                          <span
                            className={`${colors.textLight} text-lg font-medium`}
                          >
                            Plot #
                            {viewDetails[0].plot_id?.plot_number || "Unknown"}
                          </span>
                        </div>
                        <div className={`${colors.textMuted} text-sm`}>
                          {viewDetails[0].plot_id?.marla || "Unknown"} sqft
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className={`${colors.textMuted}`}>Block: </span>
                          <span className={`${colors.textLight}`}>
                            {viewDetails[0].plot_id?.block || "Unknown"}
                          </span>
                        </div>
                        <div>
                          <span className={`${colors.textMuted}`}>
                            Total Value:{" "}
                          </span>
                          <span className={`${colors.textLight}`}>
                            PKR
                            {viewDetails[0].plot_id?.price?.toLocaleString() ||
                              "Unknown"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Installments Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`${colors.textLight} text-lg font-medium`}
                      >
                        Overdue Installments
                      </div>
                      <div
                        className={`${theme.tailwind.bg1} border ${theme.tailwind.border16} px-3 py-1 rounded-full`}
                      >
                        <span
                          className={`${theme.tailwind.text15} text-sm font-medium`}
                        >
                          {viewDetails.length} Payments
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Scrollable Cards Section */}
              {viewDetails && viewDetails.length > 0 && (
                <div className="overflow-y-auto px-6 flex-grow">
                  <div className="space-y-3 pb-6">
                    {viewDetails.map((installment) => {
                      const severity = getInstallmentSeverity(installment);
                      return (
                        <div
                          key={installment._id}
                          className={`p-4 rounded-xl bg-slate-800/60 border ${colors.borderAccent} flex flex-col md:flex-row md:items-center justify-between gap-4`}
                        >
                          <div className="space-y-1">
                            <div className={`${colors.textLight} font-medium`}>
                              Payment #{installment.installment_number}
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar
                                className={`w-4 h-4 ${colors.textMuted}`}
                              />
                              <span className={`${colors.textMuted} text-sm`}>
                                Due:{" "}
                                {new Date(
                                  installment.due_date
                                ).toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign
                                className={`w-4 h-4 ${colors.textMuted}`}
                              />
                              <span className={`${colors.textMuted} text-sm`}>
                                Amount: PKR{" "}
                                {installment.amount.toLocaleString()}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${severity.bgColor} ${severity.borderColor} border`}
                            >
                              {severity.icon}
                              <span
                                className={`text-sm font-medium ${severity.textColor}`}
                              >
                                {severity.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const overdueMonths = Math.ceil(
                                    (Date.now() - new Date(installment.due_date).getTime()) /
                                    (1000 * 60 * 60 * 24 * 30)
                                  );
                                  handleSendReminder(
                                    installment._id,
                                    installment.customer_id,
                                    installment.plot_id?.plot_number,
                                    installment.amount,
                                    overdueMonths
                                  );
                                }}
                                disabled={isReminderDisabled(installment._id)}
                                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 
                          ${isReminderDisabled(installment._id)
                                    ? `${theme.tailwind.bg31} ${colors.textDim} cursor-not-allowed`
                                    : `${colors.bgButton} ${colors.text} hover:${colors.bgButtonHover} ${colors.borderButtonHover} border ${colors.borderButton} transform hover:-translate-y-0.5 transition-all duration-300`
                                  }`}
                              >
                                <Bell className="w-4 h-4" />
                                {isReminderDisabled(installment._id)
                                  ? "Reminder Sent"
                                  : "Send Reminder"}
                              </button>
                              {/* <button
                                onClick={() => {
                                  dispatch(
                                    payInstallmentAsync(installment._id)
                                  );
                                  setViewDetails(null);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 
                          ${colors.bgButtonAccent} hover:bg-fuchsia-500/30
                          transition-all duration-300 
                          ${theme.tailwind.text13} 
                          border border-fuchsia-400/30 hover:border-fuchsia-400/50
                          shadow-lg shadow-fuchsia-500/10 hover:shadow-fuchsia-500/20
                          transform hover:-translate-y-0.5`}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Mark as Paid
                              </button> */}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fixed Footer */}
              {viewDetails && viewDetails.length > 0 && (
                <div className="p-6 border-t border-slate-700/50 flex-shrink-0">
                  <div className="flex justify-end">
                    <button
                      onClick={() => setViewDetails(null)}
                      className={`px-5 py-2.5 rounded-xl 
                ${colors.bgButton} hover:${colors.bgButtonHover}
                transition-all duration-300 
                ${colors.text} 
                border ${colors.borderButton} hover:${colors.borderButtonHover}
                shadow-lg ${colors.shadowAccent} hover:${colors.shadowHover}
                transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 ${colors.focus}`}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FollowUp Modal */}
      {isFollowUpModalOpen && selectedFollowUpData && (
        <FollowUpModal
          isOpen={isFollowUpModalOpen}
          onClose={() => {
            setIsFollowUpModalOpen(false);
            setSelectedFollowUpData(null);
          }}
          plotId={selectedFollowUpData.plotId}
          bookingId={selectedFollowUpData.bookingId}
          customerId={selectedFollowUpData.customerId}
        />
      )}
    </div>
  );
};

export default OverduePayments;
